// src/native/auth/nativeAuthConfig.js
//
// WS-2 AUTH-NATIVE. Constants and pure predicates for the native auth path.
//
// Everything here is plain data plus string parsing. There is no Capacitor
// import, no side effect at module scope, and nothing that behaves differently
// in a browser: a browser simply never calls it, because every caller is behind
// an isNative() guard.
//
// The custom URL scheme lives in src/lib/platform.js (WS-1) and is imported,
// never re-declared. If the bundle ID ever changes, exactly one line changes.

import { NATIVE_DEEP_LINK_SCHEME } from '@/lib/platform';

/** The protocol component of the app's custom URL scheme, e.g. 'com.timohelps.app:'. */
export const NATIVE_DEEP_LINK_PROTOCOL = `${NATIVE_DEEP_LINK_SCHEME}:`;

/**
 * Host component of the OAuth return URL. With a non-special scheme the WHATWG
 * URL parser reads 'com.timohelps.app://auth/callback' as host 'auth' and
 * pathname '/callback'. Verified against Node's URL implementation, which is
 * the same parser WebKit ships.
 */
export const NATIVE_AUTH_CALLBACK_HOST = 'auth';

/** Path component of the OAuth return URL. */
export const NATIVE_AUTH_CALLBACK_PATH = '/callback';

/**
 * The redirect Supabase sends the browser back to after the provider consent
 * screen. This exact string must also be listed in the Supabase dashboard under
 * Authentication > URL Configuration > Redirect URLs (INT does that manually,
 * it is purely additive and does not disturb the existing web redirect URLs).
 */
export const NATIVE_OAUTH_REDIRECT = `${NATIVE_DEEP_LINK_SCHEME}://${NATIVE_AUTH_CALLBACK_HOST}${NATIVE_AUTH_CALLBACK_PATH}`;

/**
 * Where a native user is sent to recover a forgotten password.
 *
 * Why the whole flow leaves the app instead of just the email link: Supabase
 * runs in PKCE mode (src/lib/supabase.js:14). resetPasswordForEmail stores a
 * code verifier in the localStorage of whichever origin requested it. A reset
 * requested inside the native webview stores the verifier on the native origin,
 * while the email link necessarily opens in Safari on www.timohelps.com, where
 * that verifier does not exist and the exchange fails. This is the same class of
 * bug the canonical-host redirect in AuthContext exists to prevent. Requesting
 * and completing the reset in the same system browser is the only variant that
 * actually works.
 *
 * `mode=reset` is a requested, optional convenience: Login.jsx today only reads
 * `mode === 'recovery'`, so if INT declines that patch the parameter is ignored
 * and the user lands on the normal login page and taps "Forgot password". Both
 * outcomes are correct; one is a tap shorter.
 */
export const WEB_PASSWORD_RESET_URL = 'https://www.timohelps.com/login?mode=reset';

/** Providers the native sign-in surface is allowed to start. */
export const NATIVE_OAUTH_PROVIDERS = Object.freeze(['google', 'apple', 'facebook']);

/**
 * Provider specific query params, kept identical to the web path so native and
 * web behave the same at the provider's consent screen. Web Google passes
 * prompt=select_account (AuthContext.jsx signInWithGoogle); native matches it.
 */
export const NATIVE_PROVIDER_QUERY_PARAMS = Object.freeze({
  google: Object.freeze({ prompt: 'select_account' }),
});

/** True when `provider` is one this module knows how to start natively. */
export function isNativeOAuthProvider(provider) {
  return typeof provider === 'string' && NATIVE_OAUTH_PROVIDERS.includes(provider);
}

/**
 * True when `rawUrl` is our OAuth return deep link. Never throws: an unparseable
 * or foreign URL just returns false, so a deep link belonging to some future
 * feature falls straight through to whoever else is listening.
 */
export function isNativeAuthCallbackUrl(rawUrl) {
  if (typeof rawUrl !== 'string' || rawUrl.length === 0) return false;
  try {
    const url = new URL(rawUrl);
    if (url.protocol !== NATIVE_DEEP_LINK_PROTOCOL) return false;
    if (url.host !== NATIVE_AUTH_CALLBACK_HOST) return false;
    return url.pathname === NATIVE_AUTH_CALLBACK_PATH
      || url.pathname.startsWith(`${NATIVE_AUTH_CALLBACK_PATH}/`);
  } catch {
    return false;
  }
}
